import i18n from '@/utils/i18n';
import moment from 'moment-timezone';
import validator from 'validator';
import { formatPhoneNumber, getE164PhoneNumber } from '@/utils/phone-number';
import { MICRO_CONVERSION } from '@/utils/money';
import { parseTransientAwareNumber } from '@/experiences/sales-portal/domain/pricing/screens/management/blocs/utils/numeric-input';
import {
  ProspectEditFormErrors,
  ProspectEditFormParams,
  ProspectEditFormValues,
} from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/types';
import { SalesPortalProspectDetails } from '@/types';
import { useState } from 'react';

export interface ProspectEditFormResult {
  editedFields: Partial<Record<keyof ProspectEditFormValues, boolean>>;
  errors: ProspectEditFormErrors;
  hasValidationErrors: boolean;
  hydrate: (data: SalesPortalProspectDetails) => void;
  isDirty: boolean;
  setFieldTouched: (field: keyof ProspectEditFormValues) => void;
  setFormField: <K extends keyof ProspectEditFormValues>(
    field: K,
    value: ProspectEditFormValues[K],
  ) => void;
  values: ProspectEditFormValues;
}

const INITIAL_FORM_VALUES: ProspectEditFormValues = {
  closeDate: null,
  competitors: '',
  contactEmail: '',
  contactName: '',
  contactPhone: '',
  contactRole: '',
  weeklyValue: '',
};

const parseCloseDate = (value: string): Date | null => {
  const parsed = moment(value, 'YYYY-MM-DD', true);
  return parsed.isValid()
    ? new Date(parsed.year(), parsed.month(), parsed.date())
    : null;
};

const toFormValues = (
  data: SalesPortalProspectDetails,
): ProspectEditFormValues => ({
  closeDate: data.estimated_close_date?.value
    ? parseCloseDate(data.estimated_close_date.value)
    : null,
  competitors: (data.incumbents?.value ?? []).join(', '),
  contactEmail: data.contact_email?.value ?? '',
  contactName: data.contact_name?.value ?? '',
  contactPhone: data.contact_phone?.value
    ? formatPhoneNumber(data.contact_phone.value)
    : '',
  contactRole: data.contact_role?.value ?? '',
  weeklyValue:
    data.weekly_estimated_value_in_micros?.value !== null &&
    data.weekly_estimated_value_in_micros?.value !== undefined
      ? String(data.weekly_estimated_value_in_micros.value / MICRO_CONVERSION)
      : '',
});

/**
 * Hook for managing state, validation and hydration of the prospect details editor form.
 *
 * Field validation error messages are displayed after blur, and clear once changes are made.
 *
 * @param params.phoneCountryCode - Country code used for phone number formatting + validation
 * @returns {@link ProspectEditFormResult} Object containing:
 * - values: Current form values
 * - setFormField: Function to update a form field value
 * - setFieldTouched: Function to mark a field as touched
 * - hydrate: Function to populate the form with existing prospect data
 * - editedFields: Fields the user has edited since hydration, to send on save
 * - isDirty: Boolean indicating if the form has unsaved edits
 * - errors: Object containing validation errors to display
 * - hasValidationErrors: Boolean indicating if any field is currently invalid
 */
export const useProspectEditForm = ({
  phoneCountryCode,
}: ProspectEditFormParams): ProspectEditFormResult => {
  const [values, setValues] =
    useState<ProspectEditFormValues>(INITIAL_FORM_VALUES);
  const [editedFields, setEditedFields] = useState<
    Partial<Record<keyof ProspectEditFormValues, boolean>>
  >({});
  const [touched, setTouched] = useState<
    Partial<Record<keyof ProspectEditFormValues, boolean>>
  >({});

  const setFormField = <K extends keyof ProspectEditFormValues>(
    field: K,
    value: ProspectEditFormValues[K],
  ) => {
    setValues((previous) => ({ ...previous, [field]: value }));
    setEditedFields((previous) => ({ ...previous, [field]: true }));
    setTouched((previous) => ({ ...previous, [field]: false }));
  };

  const hydrate = (data: SalesPortalProspectDetails) => {
    setValues(toFormValues(data));
    setEditedFields({});
    setTouched({});
  };

  const setFieldTouched = (field: keyof ProspectEditFormValues) => {
    setTouched((previous) => ({ ...previous, [field]: true }));
  };

  const validateForm = (
    formValues: ProspectEditFormValues,
  ): ProspectEditFormErrors => {
    const validationErrors: ProspectEditFormErrors = {};

    const contactEmail = formValues.contactEmail.trim();
    if (contactEmail && !validator.isEmail(contactEmail)) {
      validationErrors.contactEmail = i18n.t(
        'sales_portal.edit_prospect.validation.contact_email',
      );
    }

    const contactPhone = formValues.contactPhone.trim();
    if (contactPhone && !getE164PhoneNumber(contactPhone, phoneCountryCode)) {
      validationErrors.contactPhone = i18n.t(
        'sales_portal.edit_prospect.validation.contact_phone',
      );
    }

    const weeklyValue = formValues.weeklyValue.trim();
    if (weeklyValue && parseTransientAwareNumber(weeklyValue) === null) {
      validationErrors.weeklyValue = i18n.t(
        'sales_portal.edit_prospect.validation.amount',
      );
    }

    return validationErrors;
  };

  const allErrors = validateForm(values);

  const hasValidationErrors = Object.keys(allErrors).some(
    (field) => editedFields[field as keyof ProspectEditFormValues],
  );

  const errors: ProspectEditFormErrors = {
    contactEmail: touched.contactEmail ? allErrors.contactEmail : undefined,
    contactPhone: touched.contactPhone ? allErrors.contactPhone : undefined,
    weeklyValue: touched.weeklyValue ? allErrors.weeklyValue : undefined,
  };

  const isDirty = Object.keys(editedFields).length > 0;

  return {
    editedFields,
    errors,
    hasValidationErrors,
    hydrate,
    isDirty,
    setFieldTouched,
    setFormField,
    values,
  };
};
